import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, BrowserRouter } from 'react-router-dom';
import './App.css';
import Layout from './components/Layout';
import PatientList from './components/PatientList';
import api from './api/axios';
import PatientForm from './components/PatientForm';
import LoginPage from './pages/LoginPage';
import { NavLink } from 'react-router-dom';
import DashBoard from './components/DashBoard'
import OperatorsManagement from './components/OperatorsManagement';
import EquipmentManager from './components/EquipmentManager';
import EyeAIUsageReport from './components/EyeAIUsageReport';
import PatientManagement from './components/PatientManagement';
import ProfileUpdate from './components/ProfileUpdate';
import { setSessionExpiredHandler } from "./api/axios";
import CdpsManagement from './components/CdpsManagement';
import CdpUsageReport from './components/CdpUsageReport';
import OperatorUsageReport from './components/OperatorUsageReport';
import DeviceUsageReport from './components/DeviceUsageReport';
import AdminManagement from './components/AdminManagement ';
import DistributorsManagement from './components/DistributorsManagement';
import { ProfileProvider } from './contexts/ProfileContext';
import ChangeLog from './components/ChangeLog';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
// import LoginRedirect from './components/LoginRedirect';
import { useProfile } from './contexts/ProfileContext';

const MainApp = ({ onLogout, mustChangePassword, onPasswordChanged }) => {
  const location = useLocation();
  const { profile, loading } = useProfile();
  //const showPatientHeader = location.pathname.startsWith('/patient/') || location.pathname === '/';

  if (loading) {
    return <div>Loading user information...</div>;
  }

  if (mustChangePassword) {
    return (
      <Routes>
        <Route
          path="/change-password"
          element={<ForceChangePasswordPage onPasswordChanged={onPasswordChanged} />}
        />
        <Route path="*" element={<Navigate to="/change-password" replace />} />
      </Routes>
    );
  }

  return (
    <Layout onLogout={onLogout}>
      <Routes>
        {/* Routes available to all logged-in users */}
        {/* <Route path="/login-redirect" element={<LoginRedirect />} /> */}
        <Route path="/profile" element={<ProfileUpdate />} />
        <Route path="/changelog" element={<ChangeLog />} />

        {profile.userGroup === 'DEVICE' ? (
          <>
            {/* Routes for DEVICE user group */}
            <Route path="/" element={<PatientManagement key={location.key}/>} />
            <Route path="/dashboard" element={<DashBoard />} />
            <Route path="/devicereport" element={<DeviceUsageReport />} />
            {/* Redirect any other route to the default page for DEVICE */}
            <Route path="*" element={<Navigate to="/" />} />
          </>
        ) : (
          <>
            {/* Routes for other user groups */}
            {profile.userGroup === 'OPERATOR' && (
              <Route path="/patient" element={<PatientManagement key={location.key}/>} />
            )}
            <Route path="/" element={<DashBoard />} />
            <Route path="/operators" element={<OperatorsManagement />} />
            <Route path="/equipment" element={<EquipmentManager />} />
            <Route path="/report" element={<CdpUsageReport />} />
            <Route path="/opreport" element={<OperatorUsageReport />} />
            <Route path="/users/country-partners" element={<CdpsManagement />} />
            <Route path="/users/distributors" element={profile.userGroup === 'ADMIN' ? <DistributorsManagement /> : <Navigate to="/" />} />
            <Route path="/users/administrators" element={<AdminManagement />} />
            <Route path="*" element={<Navigate to="/" />} />
          </>
        )}
      </Routes>
    </Layout>
  );
};

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start in a loading state
  const [mustChangePassword, setMustChangePassword] = useState(
    () => window.sessionStorage.getItem('mustChangePassword') === 'true'
  );
  const navigate = useNavigate();

  // useEffect(() => {
  //   // This function runs when the app first loads
  //   const verifySession = async () => {
  //     try {
  //       // API Call to your backend to check the session
  //       const response = await api.get('webapi/statuspolling');

  //       // Assuming a 200 OK response means the session is valid.
  //       // You might need to adjust this based on your API's response.
  //       if (response.status === 200) {
  //         setIsLoggedIn(true);
  //       }
  //     } catch (error) {
  //       // If the request fails, the user is not logged in.
  //       console.error('Session verification failed:', error);
  //     } finally {
  //       // Stop loading, whether the check succeeded or failed.
  //       setIsLoading(false);
  //     }
  //   };

  //   verifySession();
  // }, []); // The empty array ensures this runs only once on mount

  useEffect(() => {
  const verifySession = async () => {
    try {
      const res = await api.get("webapi/statuspolling");
      if (res.status === 200) setIsLoggedIn(true);
    } catch (err) {
      setIsLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  };

  verifySession();
}, []);

  useEffect(() => {
  setSessionExpiredHandler(() => {
    setIsLoggedIn(false);       // inform React state
    setMustChangePassword(false);
    window.sessionStorage.removeItem('mustChangePassword');
    navigate("/login");         // safe redirect, no loop
  });

  return () => setSessionExpiredHandler(null);
}, [navigate]);

  const handleLogin = ({ targetPath = '/', mustChangePassword: nextMustChangePassword = false } = {}) => {
    // After a successful login, the backend has created a session.
    // We just need to update the frontend state.
    setIsLoggedIn(true);
    setMustChangePassword(nextMustChangePassword);
    if (nextMustChangePassword) {
      window.sessionStorage.setItem('mustChangePassword', 'true');
    } else {
      window.sessionStorage.removeItem('mustChangePassword');
    }
    navigate(targetPath || '/');
  };

  const handlePasswordChanged = () => {
    setMustChangePassword(false);
    window.sessionStorage.removeItem('mustChangePassword');
    navigate('/');
  };

  // It's good practice to have a way to log out.
  // This function would be called from a "Logout" button in your Layout component.
  const handleLogout = async () => {
    try {
      // Make an API call to your backend to invalidate the session
      await api.post('logout');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggedIn(false);
      setMustChangePassword(false);
      window.sessionStorage.removeItem('mustChangePassword');
      navigate('/login');
    }
  };

  // While checking the session, you can show a loading spinner
  // to prevent a flash of the login page.
  if (isLoading) {
    return <div>Loading...</div>; // Or a proper spinner component
  }

  // Only render the routes after the initial session check is complete.
  // This prevents components from making API calls before we know if the user is authenticated.
  return (
    !isLoading && (
      <Routes>
        {isLoggedIn ? (
          // If logged in, render the main app and protect all routes
          <Route path="/*" element={
            <ProfileProvider key={String(mustChangePassword)}>
              <MainApp
                onLogout={handleLogout}
                mustChangePassword={mustChangePassword}
                onPasswordChanged={handlePasswordChanged}
              />
            </ProfileProvider>
          } />
        ) : (
          // If not logged in, only allow access to the login page and redirect all other paths
          <>
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        )}
      </Routes>
    )
  );

};

export default App;
